<script setup lang="ts">

import { apps, formatting, useLocalization, useOrderViewContext } from "@hantera/portal-app";
import { computed, ref } from "vue";
import { ActivityLog, ActivityLogEntry, ToggleSwitch } from "@hantera/design-system";
import AppSlotView from "@/AppSlotView.vue";
import DeliveryCreated from "@/components/Order/Timeline/DeliveryCreated.vue";
import InvoiceCreated from "@/components/Order/Timeline/InvoiceCreated.vue";
import CheckpointEntry from "@/components/Order/Timeline/CheckpointEntry.vue";

interface TimelineEntry {
  id: string
  timestamp: Date
  component: any
  props: any
  identity?: {
    identityId: string
    type: 'system' | 'user' | 'oauth_client'
    name?: string
  }
}

interface TimelineDate {
  date: Date
  entries: TimelineEntry[]
}

const orderViewContext = useOrderViewContext()
const timeline = ref<TimelineDate[]>([]);
const t = useLocalization();
const showCheckpoints = ref(orderViewContext.timetravelState.isActive);

const entries = computed(() => {

  const result = [...orderViewContext.state.activityLogs]

  // Add checkpoints if toggle is enabled
  if (showCheckpoints.value && orderViewContext.state.checkpoints) {
    orderViewContext.state.checkpoints.slice(1).forEach(checkpoint => {
      result.push({
        activityLogId: 'checkpoint_' + checkpoint.checkpointId,
        createdAt: checkpoint.timestamp,
        type: 'checkpoint',
        messageTemplate: '',
        dynamic: {
          checkpoint
        },
        identity: checkpoint.identity
      });
    });
  }

  orderViewContext.state.deliveries.forEach(delivery => {
    result.push({
      activityLogId: 'delivery_' + delivery.deliveryId,
      createdAt: delivery.createdAt,
      type: 'deliveryCreated',
      messageTemplate: '',
      dynamic: {
        delivery
      }
    });
  });

  orderViewContext.state.invoices.forEach(invoice => {
    result.push({
      activityLogId: 'invoice_' + invoice.invoiceId,
      createdAt: invoice.createdAt,
      type: 'invoiceCreated',
      messageTemplate: '',
      dynamic: {
        invoice
      }
    });

    orderViewContext.state.payments.forEach(payment => {

      const entries = payment.journalEntries.filter(e => e.invoiceId === invoice.invoiceId);

      entries.forEach(e => {

        if (e.paymentJournalEntryType === 'capture') {
          result.push({
            activityLogId: 'invoice_' + invoice.invoiceId + '_' + e.paymentJournalEntryId,
            createdAt: e.timestamp,
            type: 'paymentCharge',
            messageTemplate: 'Captured {amount} for {invoiceNumber} from {paymentNumber}',
            dynamic: {
              amount: formatting.currency(e.amount, orderViewContext.state.currencyCode),
              invoiceNumber: invoice.invoiceNumber,
              paymentNumber: payment.paymentNumber
            }
          })
        } else {
          result.push({
            activityLogId: 'invoice_' + invoice.invoiceId + '_' + e.paymentJournalEntryId,
            createdAt: e.timestamp,
            type: 'paymentRefund',
            messageTemplate: 'Refunded {amount} for {invoiceNumber} from {paymentNumber}',
            dynamic: {
              amount: formatting.currency(e.amount, orderViewContext.state.currencyCode),
              invoiceNumber: invoice.invoiceNumber,
              paymentNumber: payment.paymentNumber
            }
          })
        }
      })
    })
  })

  return result
})

const componentMapper = (entry: ActivityLogEntry) => {
  switch (entry.type) {
    case 'deliveryCreated':
      return DeliveryCreated
    case 'invoiceCreated':
      return InvoiceCreated
    case 'checkpoint':
      return CheckpointEntry
  }
}

</script>

<template>

  <div class="toolbar neat-scroll">
    <label class="checkpoint-toggle">
      <ToggleSwitch type="checkbox" v-model="showCheckpoints" />
      Display checkpoints
    </label>
    <AppSlotView :slot="apps.orderViewSlots.toolbars.timeline" />
  </div>
  
  <ActivityLog :entries="entries" :component-mapper="componentMapper" />

</template>


<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--color-border);
}

.checkpoint-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkpoint-toggle input[type="checkbox"] {
  cursor: pointer;
}
</style>
